import { useMemo } from 'react';
import { Badge } from '@/common/components/Badge/Badge';
import { CardAction } from '@/common/components/CardAction/CardAction';
import { TCardActionLinkProps } from '@/common/components/CardActionLink/CardActionLink';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import { ROUTES } from '@/common/constants/routes';
import { ICard } from '@/common/integrations/menhir/employee/card/card.types';
import { usePathname, useRouter } from 'expo-router';
import {
  getBackgroundColor,
  getBadgeText,
  getDescription,
  getIconBadgeColor,
  getIllustration,
  getTitle,
} from './CardStatusBanner.utils';

export type TCardStatusBannerProps = Partial<TCardActionLinkProps> & {
  card: ICard;
};

export const CardStatusBanner = ({
  card,
  ...cardProps
}: TCardStatusBannerProps) => {
  const router = useRouter();
  const pathname = usePathname();

  const badgeFill = useMemo(() => {
    if (card.status === 'active' && card.isExpiring) {
      return 'warning';
    }
    if (card.format === 'digital' && card.status === 'active') {
      return 'success';
    }
    switch (card.status) {
      case 'active':
        return 'success';
      case 'cancelled':
        return 'disabled';
      case 'locked':
        return 'info';
      case 'expired':
        return 'error';
      default:
        return undefined;
    }
  }, [card]);

  const title = getTitle(card);
  const description = getDescription(card);

  return (
    <CardAction
      {...cardProps}
      title={title}
      description={description}
      illustration={getIllustration(card)}
      icon={
        card.status !== 'inactive' ? (
          <Icon icon="credit-card" color="neutral10" />
        ) : undefined
      }
      iconBadgeColor={getIconBadgeColor(card)}
      action={
        <Stack align="center" gap="md" horizontal>
          {badgeFill && (
            <Badge status={badgeFill} size="md">
              {getBadgeText(card)}
            </Badge>
          )}
          <Icon icon="chevron-right" />
        </Stack>
      }
      backgroundColor={getBackgroundColor(card)}
      onPress={() => {
        router.navigate({
          pathname: ROUTES.CARD,
          params: {
            origin: pathname,
            cardId: card.id,
          },
        });
      }}
    />
  );
};
